import { useDeleteFileMutation } from "#features/conversations/api/useDeleteFileMutation.ts";
import { useGenerateConversationFileUploadUrlMutation } from "#features/conversations/api/useGenerateConversationFileUploadUrlMutation.ts";
import { useGetConversationFilesPollingQuery } from "#features/conversations/api/useGetConversationFilesPollingQuery.ts";
import { useUploadFileMutation } from "#features/conversations/api/useUploadFileMutation.ts";
import { getProcessingFileIds, withProcessingFlag } from "#features/conversations/hooks/fileProcessing.ts";
import { useGetOrCreateConversationId } from "#features/conversations/hooks/useGetOrCreateConversationId.ts";
import { useComposerDraftStore } from "#features/conversations/stores/useComposerDraftStore.ts";
import type { FileModel } from "#features/conversations/types/FileModel.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

const MAX_FILES_COUNT = 10;

export const useUploadFiles = (existingConversationId?: string) => {
    const [files, setFiles] = useState<FileModel[]>([]);
    const { tShared } = useSharedTranslation();
    const { conversationId, getOrCreateConversationId } = useGetOrCreateConversationId(existingConversationId);
    const { mutate: generateFileUploadUrl } = useGenerateConversationFileUploadUrlMutation(setFiles, {
        onSuccess: (fileId, fileUploadUrl, file) => uploadFile({ fileId, fileUploadUrl, file })
    });
    const { mutate: uploadFile } = useUploadFileMutation(setFiles, conversationId);
    const { mutate: deleteFile } = useDeleteFileMutation();

    // The files of a reply that failed to send come back with its text (see useComposerDraftStore).
    const unsentFiles = useComposerDraftStore(state =>
        (existingConversationId !== undefined ? state.unsentFiles[existingConversationId] : undefined));
    const clearUnsentFiles = useComposerDraftStore(state => state.clearUnsentFiles);
    useEffect(() => {
        if (unsentFiles !== undefined && existingConversationId !== undefined) {
            setFiles(current => [...unsentFiles, ...current]);
            clearUnsentFiles(existingConversationId);
        }
    }, [unsentFiles, existingConversationId, clearUnsentFiles]);

    // Poll the files still awaiting indexation. The query stops itself (refetchInterval → false) and
    // re-keys as files reach a terminal state. There is no client budget: send stays gated until every
    // file is retrievable or Failed (see useGetConversationFilesPollingQuery); a stuck file is escaped
    // by removing it.
    const processingFileIds = useMemo(() => getProcessingFileIds(files), [files]);
    const { data: polledFiles } = useGetConversationFilesPollingQuery(processingFileIds, {
        enabled: processingFileIds.length > 0
    });

    // Persist poll results (availability, plus isIndexed/uploadStatus for other consumers) back onto the
    // tracked files. Writing them into state — rather than deriving from the query — keeps them across
    // query re-keys when another file is attached, so an already-terminal file never flickers back to
    // "processing". Short-circuits when nothing changed so steady-state polls don't re-render consumers.
    useEffect(() => {
        if (!polledFiles) {
            return;
        }

        setFiles(prevFiles => {
            let changed = false;
            const nextFiles = prevFiles.map(prevFile => {
                const polled = polledFiles.find(file => file.id === prevFile.id);

                if (!polled || (
                    prevFile.isIndexed === polled.isIndexed
                    && prevFile.fileUploadStatus === polled.uploadStatus
                    && prevFile.availability === polled.availability
                )) {
                    return prevFile;
                }

                changed = true;

                return { ...prevFile, fileUploadStatus: polled.uploadStatus, isIndexed: polled.isIndexed, availability: polled.availability };
            });

            return changed ? nextFiles : prevFiles;
        });
    }, [polledFiles]);

    const handleFileSelected = useCallback(async (selectedFiles: File[]) => {
        if (files.length + selectedFiles.length > MAX_FILES_COUNT) {
            toast.error(tShared("maxFilesCountExceeded", { maxFilesCount: MAX_FILES_COUNT }));

            return;
        }

        const conversationIdForUpload = await getOrCreateConversationId();

        if (!conversationIdForUpload) {
            // Conversation creation failed; the error toast was already shown.
            return;
        }

        selectedFiles.forEach(file =>
            generateFileUploadUrl({ file, abortController: new AbortController(), conversationId: conversationIdForUpload })
        );
    }, [files.length, generateFileUploadUrl, getOrCreateConversationId, tShared]);

    const removeFile = useCallback((file: FileModel) => {
        setFiles(files.filter(newFile => newFile.id !== file.id));

        if (file.fileUploadStatus === "Completed") {
            // If the file upload has been completed, remove the file on the backend.
            deleteFile({ fileId: file.id });
        } else if (file.fileUploadStatus === "Uploading") {
            // If the file is currently uploading, abort the upload.
            // This will trigger the onError callback in useUploadFileMutation.
            file.abortController?.abort();
        }
    }, [deleteFile, files]);

    const removeAllFiles = useCallback(() => {
        files.forEach(file => {
            if (file.fileUploadStatus === "Uploading") {
                file.abortController?.abort();
            }
        });

        setFiles([]);
    }, [files]);

    const filesWithProcessing = useMemo(() => withProcessingFlag(files), [files]);

    return {
        files: filesWithProcessing,
        handleFileSelected,
        removeFile,
        removeAllFiles,
        conversationId,
        getOrCreateConversationId
    };
};
